import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAppConfig } from "../ConfigContext";
import "../index.css";
import { getSkinSrc } from "./SeasonalSkins";
import CardPicker from "./CardPicker";
import LoadingSpinner from "./LoadingSpinner";

const API_BASE = process.env.REACT_APP_API_URL;

function normalizeRarity(rarity) {
  const r = String(rarity || "common").trim().toLowerCase();

  if (r === "legendary") return "legendary";
  if (r === "epic") return "epic";
  if (r === "rare") return "rare";
  return "common";
}

function safeParseJson(value, fallback) {
  if (value == null) return fallback;
  if (typeof value === "object") return value;

  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function parseAbilities(raw) {
  const parsed = safeParseJson(raw, []);
  return Array.isArray(parsed) ? parsed : [];
}

function sanitizeAbilityList(list) {
  return (Array.isArray(list) ? list : [])
    .map((ability) => {
      if (typeof ability === "string") return ability;
      return ability?.name || "";
    })
    .map((ability) => String(ability || "").trim())
    .filter(Boolean);
}

function getCardCharacter(card) {
  const parsed = safeParseJson(card?.character, {});
  return parsed && typeof parsed === "object" ? parsed : {};
}

function getCardPiecesTotal(card) {
  return (
    Number(
      card?.pieces ??
        card?.card_pieces ??
        card?.cardPieces ??
        card?.fragments ??
        0
    ) || 0
  );
}

function getSingleMergeBasePrice(row) {
  const sparks = Number(row?.sparks ?? 0) || 0;
  const runes = Number(row?.runes ?? 0) || 0;

  if (sparks > 0 && runes > 0) {
    return { currency: null, amount: 0, invalid: true };
  }

  if (sparks > 0) {
    return { currency: "sparks", amount: sparks, invalid: false };
  }

  if (runes > 0) {
    return { currency: "runes", amount: runes, invalid: false };
  }

  return { currency: null, amount: 0, invalid: true };
}

function computeMergePriceByLevel(basePrice, cardA, cardB) {
  if (!cardA || !cardB || basePrice?.invalid) {
    return { currency: null, amount: 0, invalid: true };
  }

  const maxLevel = Math.max(
    Number(cardA?.level || 1),
    Number(cardB?.level || 1)
  );

  const multiplier = 1 + (maxLevel - 1) * 0.1;

  return {
    ...basePrice,
    baseAmount: Number(basePrice?.amount || 0),
    multiplier: Number(multiplier.toFixed(2)),
    amount: Math.ceil(Number(basePrice?.amount || 0) * multiplier),
    levelReference: maxLevel,
  };
}

function getMergeErrorMessage(error) {
  switch (error) {
    case "CARD_NOT_FOUND":
      return "One of these cards could not be found.";
    case "NOT_YOUR_CARD":
      return "One of these cards does not belong to you.";
    case "CARD_RARITY_MISMATCH":
      return "Both cards must have the same rarity.";
    case "CARD_FOR_SALE":
      return "Cards listed for sale cannot be merged.";
    case "CARD_ALREADY_MERGED":
      return "One of these cards was already merged.";
    case "INSUFFICIENT_SPARKS":
      return "Not enough Sparks.";
    case "INSUFFICIENT_RUNES":
      return "Not enough Runes.";
    case "MISSING_OR_INVALID_CURRENCY":
      return "Merge price is not configured correctly.";
    default:
      return error || "Failed to merge cards.";
  }
}

function CharacterPreview({ card }) {
  const character = getCardCharacter(card);

  return (
    <div
      className="char_bg"
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        zIndex: 2,
      }}
    >
      {Object.entries(character)
        .sort(([a], [b]) => {
          const order = [
            "background",
            "body",
            "boots",
            "pants",
            "shirt",
            "neck",
            "face",
            "eyes",
            "mouth",
            "head",
            "weapons",
          ];

          const indexA = order.includes(a) ? order.indexOf(a) : order.length;
          const indexB = order.includes(b) ? order.indexOf(b) : order.length;

          return indexA - indexB;
        })
        .map(([part, data]) => {
          if (!data || typeof data !== "object") return null;

          const defaultSrc = `/assets/${part}/${data.side}/${data.rarity}/${data.variant}_thumb.png`;

          const seasonName =
            card?.seasonal_accessory?.season_name ||
            card?.season_name ||
            null;

          const src = getSkinSrc(seasonName, part, defaultSrc);

          const zIndexMap = {
            background: 1,
            body: 2,
            boots: 3,
            pants: 4,
            shirt: 5,
            neck: 6,
            face: 7,
            eyes: 8,
            mouth: 9,
            head: 10,
            weapons: 11,
          };

          return (
            <img
              key={part}
              src={src}
              alt={part}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: part === "background" ? "cover" : "contain",
                zIndex: zIndexMap[part] ?? 10,
                pointerEvents: "none",
              }}
            />
          );
        })}
    </div>
  );
}

function MergeSlot({ card, onClick, onClear }) {
  const abilitiesSafe = sanitizeAbilityList(parseAbilities(card?.abilities));

  if (!card) {
    return (
      <div
        onClick={onClick}
        role="button"
        tabIndex={0}
        style={{
          backgroundImage: 'url("/v2/nocard.png"), url("/v2/item.png")',
          backgroundRepeat: "no-repeat, no-repeat",
          backgroundSize: "50%, 100% 100%",
          backgroundPosition: "center center, center center",
          padding: 3,
          paddingBottom: 6,
          position: "relative",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          minHeight: 165,
          cursor: "pointer",
        }}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClick?.();
          }}
          className="game-button-small yellow-button"
        >
          SELECT
        </button>
      </div>
    );
  }

  return (
    <div
      onClick={() => onClick?.()}
      role="button"
      tabIndex={0}
      style={{
        backgroundImage: 'url("/v2/item.png")',
        padding: 6,
        backgroundRepeat: "repeat",
        backgroundSize: "100% 100%",
        backgroundPosition: "center center",
        position: "relative",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        cursor: "pointer",
      }}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClear?.();
        }}
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 80,
          width: 28,
          height: 28,
          padding: 0,
          border: "none",
          background: "transparent",
          cursor: "pointer",
        }}
      >
        <img
          src="/v2/close.png"
          alt="Remove"
          style={{
            width: "100%",
            height: "100%",
            display: "block",
            pointerEvents: "none",
          }}
        />
      </button>

     <div
      style={{
        borderRadius: 5,
        width: "100%",
        aspectRatio: 1,
        cursor: "default",
        position: "relative",
        overflow: "hidden",
      }}
    >
        <div
          style={{
            position: "absolute",
            top: 6,
            right: 5,
            zIndex: 30,
            display: "flex",
            flexDirection: "column",
            gap: 3,
            pointerEvents: "none",
          }}
        >
          {abilitiesSafe.slice(0, 3).map((ability, idx) => {
            const abilityKey = String(ability || "").toLowerCase();

            return (
              <div
                key={`${ability}-${idx}`}
                style={{
                  width: 15,
                  height: 15,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                title={ability}
              >
                <img
                  src={`/a_${abilityKey}.png`}
                  alt={ability}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                  style={{
                    width: 15,
                    height: 15,
                    objectFit: "contain",
                    pointerEvents: "none",
                  }}
                />
              </div>
            );
          })}
        </div>

        <div
          style={{
            position: "absolute",
            top: 5,
            left: 5,
            width: 28,
            height: 28,
            backgroundImage: "url('/v2/position.png')",
            backgroundSize: "100% 100%",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            color: "#fff",
            fontSize: 15,
            lineHeight: "28px",
            textAlign: "center",
            zIndex: 20,
            fontFamily: '"Fredoka", sans-serif',
            fontWeight: 600,
          }}
        >
          {card.level ?? 1}
        </div>

        <CharacterPreview card={card} />

        <div
          style={{
            zIndex: 20,
            textTransform: "uppercase",
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            color: "#fff",
            height: 21,
            fontSize: 11,
            lineHeight: "24px",
            textAlign: "center",
            fontWeight: 600,
            background: "rgba(77, 44, 36, 0.5)",
          }}
        >
          {card.rarity || "common"}
        </div>
      </div>

      <div
        style={{
          zIndex: 20,
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-around",
          color: "#fff",
          fontSize: 14,
          lineHeight: "15px",
          textShadow: "1px 1px #000",
          fontWeight: 600,
          height: 28,
          lineHeight: "28px",
        }}
      >
        <div
          style={{
            paddingLeft: "18px",
            backgroundImage: "url('/s_attack.png')",
            backgroundSize: "15px",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "left center",
          }}
        >
          {Number(card.attack || 0)}
        </div>

        <div
          style={{
            paddingLeft: "18px",
            backgroundImage: "url('/s_defense.png')",
            backgroundSize: "15px",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "left center",
          }}
        >
          {Number(card.defense || 0)}
        </div>

        <div
          style={{
            paddingLeft: "18px",
            backgroundImage: "url('/s_health.png')",
            backgroundSize: "15px",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "left center",
          }}
        >
          {Number(card.health || 0)}
        </div>
      </div>
    </div>
  );
}

function SuccessCardPreview({ card }) {
  const abilitiesSafe = sanitizeAbilityList(parseAbilities(card?.abilities));

  if (!card) return null;

  return (
    <div
      style={{
        backgroundImage: 'url("/v2/item.png")',
        padding: 6,
        backgroundRepeat: "repeat",
        backgroundSize: "100% 100%",
        backgroundPosition: "center center",
        position: "relative",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        width: "100%",
        maxWidth: 230,
        margin: "0 auto 12px",
      }}
    >
      <div
        style={{
          borderRadius: 5,
          width: "100%",
          aspectRatio: 1,
          cursor: "default",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 6,
            right: 5,
            zIndex: 30,
            display: "flex",
            flexDirection: "column",
            gap: 3,
            pointerEvents: "none",
          }}
        >
          {abilitiesSafe.slice(0, 3).map((ability, idx) => {
            const abilityKey = String(ability || "").toLowerCase();

            return (
              <div
                key={`${ability}-${idx}`}
                style={{
                  width: 18,
                  height: 18,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                title={ability}
              >
                <img
                  src={`/a_${abilityKey}.png`}
                  alt={ability}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                  style={{
                    width: 18,
                    height: 18,
                    objectFit: "contain",
                    pointerEvents: "none",
                  }}
                />
              </div>
            );
          })}
        </div>

        <div
          style={{
            position: "absolute",
            top: 6,
            left: 5,
            width: 32,
            height: 32,
            backgroundImage: "url('/v2/position.png')",
            backgroundSize: "100% 100%",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            color: "#fff",
            fontSize: 17,
            lineHeight: "32px",
            textAlign: "center",
            zIndex: 20,
            fontFamily: '"Fredoka", sans-serif',
            fontWeight: 600,
          }}
        >
          {card.level ?? 1}
        </div>

        <CharacterPreview card={card} />

        <div
          style={{
            zIndex: 20,
            textTransform: "uppercase",
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            color: "#fff",
            height: 24,
            fontSize: 12,
            lineHeight: "24px",
            textAlign: "center",
            fontWeight: 600,
            background: "rgba(77, 44, 36, 0.65)",
          }}
        >
          {card.rarity || "common"}
        </div>
      </div>

      <div
        className="shadow1"
        style={{
          zIndex: 20,
          marginTop: 6,
          color: "#fff",
          fontSize: 16,
          lineHeight: "18px",
          textAlign: "center",
          fontWeight: 600,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          width: "100%",
          height: 24,
          background: "linear-gradient(180deg, rgba(77,44,36,0.85) 0%, rgba(45,22,15,0.95) 100%)",
        }}
      >
        {card.name || `Card #${card.id}`}
      </div>

      <div
        style={{
          zIndex: 20,
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-around",
          color: "#fff",
          fontSize: 15,
          fontWeight: 600,
          height: 32,
          paddingBottom: 5
        }}
      >
        <div
          style={{
            paddingLeft: "20px",
            backgroundImage: "url('/s_attack.png')",
            backgroundSize: "17px",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "left center",
          }}
        >
          {Number(card.attack || 0)}
        </div>

        <div
          style={{
            paddingLeft: "20px",
            backgroundImage: "url('/s_defense.png')",
            backgroundSize: "17px",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "left center",
          }}
        >
          {Number(card.defense || 0)}
        </div>

        <div
          style={{
            paddingLeft: "20px",
            backgroundImage: "url('/s_health.png')",
            backgroundSize: "17px",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "left center",
          }}
        >
          {Number(card.health || 0)}
        </div>
      </div>
    </div>
  );
}

export default function CardMerge({ user, setUser, onNavigate, onMerged }) {
  const { shop } = useAppConfig();

  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const [mainCard, setMainCard] = useState(null);
  const [consumeCard, setConsumeCard] = useState(null);
  const [pickerTarget, setPickerTarget] = useState(null);

  const [error, setError] = useState("");
  const [mergeBusy, setMergeBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successData, setSuccessData] = useState(null);

  const mergeBasePrices = useMemo(
    () => ({
      common: getSingleMergeBasePrice(shop?.cardMerging?.common),
      rare: getSingleMergeBasePrice(shop?.cardMerging?.rare),
      epic: getSingleMergeBasePrice(shop?.cardMerging?.epic),
      legendary: getSingleMergeBasePrice(shop?.cardMerging?.legendary),
    }),
    [shop]
  );

  const currentMergePrice = useMemo(() => {
    if (!mainCard || !consumeCard) {
      return { currency: null, amount: 0, invalid: true };
    }

    const rarityKey = normalizeRarity(mainCard?.rarity);
    const basePrice =
      mergeBasePrices?.[rarityKey] || {
        currency: null,
        amount: 0,
        invalid: true,
      };

    return computeMergePriceByLevel(basePrice, mainCard, consumeCard);
  }, [mainCard, consumeCard, mergeBasePrices]);

  const canMerge = useMemo(() => {
    if (!mainCard || !consumeCard) return false;
    if (Number(mainCard.id) === Number(consumeCard.id)) return false;
    if (
      normalizeRarity(mainCard.rarity) !== normalizeRarity(consumeCard.rarity)
    ) {
      return false;
    }
    if (mainCard?.for_sale || consumeCard?.for_sale) return false;
    if (mainCard?.is_consumed || consumeCard?.is_consumed) return false;
    if (mainCard?.merged_into_card_id || consumeCard?.merged_into_card_id) {
      return false;
    }
    if (currentMergePrice.invalid) return false;
    return true;
  }, [mainCard, consumeCard, currentMergePrice]);

  const hasSelectedCards = !!mainCard || !!consumeCard;

  const clearSelectedCards = () => {
    if (!hasSelectedCards || mergeBusy) return;

    setMainCard(null);
    setConsumeCard(null);
    setError("");
  };

  useEffect(() => {
    let cancelled = false;

    async function loadCards() {
      try {
        setLoading(true);
        setError("");

        const token = localStorage.getItem("token");
        if (!token) throw new Error("Not logged in.");

        const res = await fetch(`${API_BASE}/api/shop/mine`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-TG-INITDATA": window.Telegram?.WebApp?.initData || "",
          },
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(data?.error || "Failed to load cards.");
        }

        if (!cancelled) {
          setCards(Array.isArray(data.cards) ? data.cards : []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Load error.");
          setCards([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadCards();

    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const openPicker = (target) => {
    setPickerTarget(target);
    setError("");
  };

  const closePicker = () => {
    setPickerTarget(null);
  };

  const handlePickedCard = ({ card }) => {
    if (!card?.id) return;

    if (card?.for_sale) {
      setError("Cards listed for sale cannot be merged.");
      closePicker();
      return;
    }

    if (card?.is_consumed || card?.merged_into_card_id) {
      setError("This card was already merged.");
      closePicker();
      return;
    }

    if (pickerTarget === "main") {
      if (consumeCard && Number(consumeCard.id) === Number(card.id)) {
        setError("You cannot select the same card twice.");
        closePicker();
        return;
      }

      if (
        consumeCard &&
        normalizeRarity(consumeCard.rarity) !== normalizeRarity(card.rarity)
      ) {
        setError("Both cards must have the same rarity.");
        closePicker();
        return;
      }

      setMainCard(card);
      setError("");
      closePicker();
      return;
    }

    if (pickerTarget === "consume") {
      if (mainCard && Number(mainCard.id) === Number(card.id)) {
        setError("You cannot select the same card twice.");
        closePicker();
        return;
      }

      if (
        mainCard &&
        normalizeRarity(mainCard.rarity) !== normalizeRarity(card.rarity)
      ) {
        setError("Both cards must have the same rarity.");
        closePicker();
        return;
      }

      setConsumeCard(card);
      setError("");
      closePicker();
    }
  };

  const confirmMerge = () => {
    if (!canMerge) {
      setError("Select two valid cards with the same rarity.");
      return;
    }

    setConfirmOpen(true);
  };

  const handleMergeCards = async () => {
    if (!canMerge || mergeBusy) return;

    try {
      setMergeBusy(true);
      setError("");

      const token = localStorage.getItem("token");
      if (!token) throw new Error("Not logged in.");

      const res = await fetch(`${API_BASE}/api/cards/merge`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-TG-INITDATA": window.Telegram?.WebApp?.initData || "",
        },
        body: JSON.stringify({
          cardAId: mainCard.id,
          cardBId: consumeCard.id,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(getMergeErrorMessage(data?.error));
      }

      const mergedCard = data.card || data.mergedCard || null;

      setCards((prev) => {
        const consumedId = Number(consumeCard.id);
        const mainId = Number(mainCard.id);

        return prev
          .filter((card) => Number(card.id) !== consumedId)
          .map((card) =>
            Number(card.id) === mainId && mergedCard
              ? { ...card, ...mergedCard }
              : card
          );
      });

      if (data.user && typeof setUser === "function") {
        setUser((prev) => ({
          ...prev,
          ...data.user,
        }));
      }

      setSuccessData({
        card: mergedCard || mainCard,
        mainCard,
        consumeCard,
      });

      setMainCard(mergedCard || null);
      setConsumeCard(null);
      setConfirmOpen(false);
      setSuccessOpen(true);
      setRefreshKey((key) => key + 1);
      onMerged?.(data);
    } catch (err) {
      setError(err.message || "Failed to merge cards.");
    } finally {
      setMergeBusy(false);
    }
  };

  return (
    <div className="modal_box">
      <div className="modal_title">
        Merge Cards

        <button
          type="button"
          className="modal_close"
          onClick={() => onNavigate?.("inventoryHub")}
        />
      </div>

      <div className="modal_body">
        <div className="modal_inside_2">
         {loading && (
  <div
    style={{
      minHeight: 360,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <LoadingSpinner text="Loading Cards" size={58} />
  </div>
)}

          {!loading && (
            <>
              
                <div
                  className="shadow2"
                  style={{
                    fontSize: 26,
                    fontWeight: 600,
                    marginBottom: 10,
                  }}
                >
                  Pick 2 Cards
                </div>

                <div
                  className="shadow1"
                  style={{
                    color: "#fff",
                    fontSize: 18,
                    fontWeight: 600,
                    lineHeight: "18px",
                    marginBottom: 15
                  }}
                >
                  The first card stays.<br/>The second card is consumed.
                </div>
              

              {error && (
                <div
                className="shadow1"
                  style={{
                    color: "#d4c342",
                    background: "rgba(0,0,0,0.35)",
                    borderRadius: 10,
                    fontWeight: 600,
                    padding: "8px 10px",
                    marginBottom: 15,
                    fontSize: 14,
                    textAlign: "center",
                  }}
                >
                  {error}
                </div>
              )}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 8,
                  marginBottom: 12,
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <MergeSlot
                  card={mainCard}
                  onClick={() => openPicker("main")}
                  onClear={() => setMainCard(null)}
                />

                <MergeSlot
                  card={consumeCard}
                  onClick={() => openPicker("consume")}
                  onClear={() => setConsumeCard(null)}
                />
              </div>

              <div
                className="info"
                style={{
                  textAlign: "center",
                  fontFamily: '"Fredoka", sans-serif',
                }}
              >
                {mainCard && consumeCard ? (
                  normalizeRarity(mainCard.rarity) ===
                  normalizeRarity(consumeCard.rarity) ? (
                    <div
                      className="shadow1"
                      style={{ color: "#fff", fontSize: 16 }}
                    >
                      
                      
                    </div>
                  ) : (
                    <div style={{ color: "#ff8a8a", fontSize: 16 }}>
                      Rarity mismatch.
                    </div>
                  )
                ) : (
                  <div
                    className="shadow1"
                    style={{ color: "#ddd", fontSize: 15 }}
                  >
                    Select two cards with the same rarity.
                  </div>
                )}

                {mainCard && consumeCard && (
                  <div
                  className="shadow1"
                    style={{
                      marginTop: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 3,
                      color: "#ffdf5f",
                      fontSize: 21,
                    }}
                  >
                    {currentMergePrice.invalid ? (
                      <strong>Merge price not configured.</strong>
                    ) : (
                      <>
                        <span style={{ color: "#fff" }}>Cost:</span>

                        <img
                          src={
                            currentMergePrice.currency === "sparks"
                              ? "/sparks.png"
                              : "/runes.png"
                          }
                          alt={currentMergePrice.currency}
                          style={{
                            width: 24,
                            height: 24,
                            verticalAlign: "middle"
                          }}
                        />{currentMergePrice.amount}
                      </>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="modal_footer">
          <button
            type="button"
            className="game-button green-button"
            disabled={!canMerge || mergeBusy}
            onClick={confirmMerge}
            style={{
              marginRight: 10,
              opacity: !canMerge || mergeBusy ? 0.5 : 1,
              cursor: !canMerge || mergeBusy ? "not-allowed" : "pointer",
            }}
          >
            {mergeBusy ? "Merging..." : "Merge Cards"}
          </button>

          <button
            type="button"
            className="game-button red-button"
            disabled={!hasSelectedCards || mergeBusy}
            onClick={clearSelectedCards}
            style={{
              opacity: !hasSelectedCards || mergeBusy ? 0.5 : 1,
              cursor: !hasSelectedCards || mergeBusy ? "not-allowed" : "pointer",
            }}
          >
            CLEAR
          </button>
        </div>
      </div>

      {pickerTarget && (
        <div>
          <div className="modal_box">
            <div className="modal_title">
              Select {pickerTarget === "main" ? "Main Card" : "Consumed Card"}

              <button
                type="button"
                className="modal_close"
                onClick={closePicker}
              />
            </div>

            <CardPicker
              user={user}
              openedVia="cardMerge"
              onClose={closePicker}
              onNavigate={onNavigate}
              onSelected={handlePickedCard}
            />
          </div>
        </div>
      )}

      <AnimatePresence>
        {confirmOpen && mainCard && consumeCard && (
          <div className="modal_box">

              <div className="modal_title">Confirm
              <button className="modal_close" onClick={() => setConfirmOpen(false)}/>
              </div>
            
            <motion.div className="modal_body"  initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>

            <div className="modal_inside">
              <img
                src="/finger.png"
                alt="Confirm Merge"
                style={{ height: 80, marginBottom: 10 }}
              />

              <div className="shadow2" style={{ fontWeight: 600, fontSize: 28, marginBottom: 10 }}>
                Confirm Merge?
              </div>

              <p
              className="shadow1"
                style={{
                  fontSize: 18,
                  textAlign: "center",
                  marginBottom: 15,
                }}
              >
                <strong>{mainCard.name || `Card #${mainCard.id}`}</strong>{" "}
                will be the{" "}
                <strong style={{ color: "#ffdf5f" }}>Main Card</strong>.
                <br />
                <strong>
                  {consumeCard.name || `Card #${consumeCard.id}`}
                </strong>{" "}
                will be <strong style={{ color: "#ff6b6b" }}>Consumed</strong>.
              </p>

              
                <div className="shadow1" style={{marginBottom: 10, fontWeight: 600}}>
                  Levels and card pieces from both cards will be added together.
                  Final rarity, stats, and abilities will be recalculated.
                </div>

                {!currentMergePrice.invalid && (
                  <div
                  className="info"
                    style={{
                      marginTop: 10,
                      fontSize: 21,
                    }}
                  >

                    Cost:&nbsp;

                    <img
                      src={currentMergePrice.currency === "sparks" ? "/sparks.png" : "/runes.png"}
                      alt={currentMergePrice.currency === "sparks" ? "Sparks" : "Runes"}
                      style={{
                        width: 24,
                        height: 24,
                        objectFit: "contain",
                        verticalAlign: "middle",
                      }}
                    />

                    <span style={{color: "#ffdf5f"}}>
                    {currentMergePrice.amount}
                    </span>
                  </div>
                )}
              </div>

              <div className="modal_footer">
                <button
                  onClick={handleMergeCards}
                  disabled={mergeBusy}
                  style={{ marginRight: 10 }}
                  className="game-button green-button"
                >
                  {mergeBusy ? "Merging..." : "Confirm"}
                </button>

                <button
                  className="game-button red-button"
                  onClick={() => setConfirmOpen(false)}
                  disabled={mergeBusy}
                >
                  CANCEL
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {successOpen && successData && (
          <div className="modal_box">

              <div className="modal_title">Merge Successful

              <button onClick={() => {
                    setSuccessOpen(false);
                    setSuccessData(null);
                  }} className="modal_close"/>

              </div>
            
            <motion.div className="modal_body" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>

            <div className="modal_inside">

              {successData.card && <SuccessCardPreview card={successData.card} />}

              <div className="info" style={{ fontSize: 18, color: "#fff" }}>
                Card Pieces: <img src="/pieces.png" style={{height: 28,verticalAlign: "middle"}}/> <span style={{color: "#ffdf5f"}}>{getCardPiecesTotal(successData.card)}</span>
              </div>
              </div>

              <div className="modal_footer">
                <button
                 className="game-button green-button"
                  onClick={() => {
                    setSuccessOpen(false);
                    setSuccessData(null);
                  }}
                >
                  Awesome
                </button>
              </div>
          </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}